// Tab fingering (§4.4). Strings are numbered from 1 = highest. Tunings are
// sounding MIDI numbers ordered high → low. Frets are relative to the capo.

export const DEFAULT_MAX_FRET = 24;

export interface FretPos {
  string: number;
  fret: number;
}

export interface FingeringContext {
  tuning: readonly number[];
  capo?: number;
  maxFret?: number;
  /** Average fretted position of the previous beats (open strings excluded). */
  handPos?: number;
  /** String of the previous single note, if any. */
  prevString?: number;
}

export function tabToMidi(string: number, fret: number, tuning: readonly number[], capo = 0): number {
  return tuning[string - 1] + capo + fret;
}

/** Fret for a sounding MIDI number on a string, or null if out of range. */
export function fretOn(midi: number, string: number, ctx: FingeringContext): number | null {
  const open = ctx.tuning[string - 1];
  if (open === undefined) return null;
  const fret = midi - open - (ctx.capo ?? 0);
  return fret >= 0 && fret <= (ctx.maxFret ?? DEFAULT_MAX_FRET) ? fret : null;
}

/** All playable positions for a sounding pitch. */
export function positionsFor(midi: number, ctx: FingeringContext): FretPos[] {
  const out: FretPos[] = [];
  for (let s = 1; s <= ctx.tuning.length; s++) {
    const f = fretOn(midi, s, ctx);
    if (f !== null) out.push({ string: s, fret: f });
  }
  return out;
}

function noteCost(pos: FretPos, ctx: FingeringContext, single: boolean): number {
  let c = 1.0 * pos.fret;
  if (ctx.handPos !== undefined) c += 0.6 * Math.abs(pos.fret - ctx.handPos);
  if (pos.fret === 0) c -= 1;
  if (single && ctx.prevString !== undefined && pos.string === ctx.prevString) c -= 0.5;
  return c;
}

/** Best position for a single sounding pitch, or null if unplayable. */
export function fingerSingle(midi: number, ctx: FingeringContext): FretPos | null {
  let best: FretPos | null = null;
  let bestCost = Infinity;
  for (const p of positionsFor(midi, ctx)) {
    const c = noteCost(p, ctx, true);
    if (c < bestCost - 1e-9 || (Math.abs(c - bestCost) < 1e-9 && best && p.string < best.string)) {
      best = p;
      bestCost = c;
    }
  }
  return best;
}

export const MAX_SPAN = 4;

function span(ps: FretPos[]): number {
  const fretted = ps.filter((p) => p.fret > 0).map((p) => p.fret);
  if (fretted.length < 2) return 0;
  return Math.max(...fretted) - Math.min(...fretted);
}

/**
 * Best injective string assignment for a chord of sounding pitches. Returns
 * positions in input order, or null when the chord cannot be fingered at all
 * (more notes than strings, or a note out of range). Assignments with a span
 * over MAX_SPAN frets are only used when nothing else exists.
 */
export function fingerChord(midis: readonly number[], ctx: FingeringContext): (FretPos | null)[] | null {
  if (midis.length === 0) return [];
  if (midis.length === 1) {
    const p = fingerSingle(midis[0], ctx);
    return p ? [p] : null;
  }
  const options = midis.map((m) => positionsFor(m, ctx));
  if (midis.length > ctx.tuning.length || options.some((o) => o.length === 0)) return null;

  let best: FretPos[] | null = null;
  let bestCost = Infinity;
  let bestWide: FretPos[] | null = null;
  let bestWideCost = Infinity;
  const used = new Set<number>();
  const cur: FretPos[] = [];

  const rec = (i: number, acc: number) => {
    if (i === midis.length) {
      const sp = span(cur);
      const total = acc + 0.5 * sp;
      if (sp <= MAX_SPAN) {
        if (total < bestCost) {
          bestCost = total;
          best = cur.slice();
        }
      } else if (total < bestWideCost) {
        bestWideCost = total;
        bestWide = cur.slice();
      }
      return;
    }
    for (const p of options[i]) {
      if (used.has(p.string)) continue;
      used.add(p.string);
      cur.push(p);
      rec(i + 1, acc + noteCost(p, ctx, false));
      cur.pop();
      used.delete(p.string);
    }
  };
  rec(0, 0);
  return best ?? bestWide;
}

/** Average fretted position (ignoring open strings) of a list of positions; undefined if none. */
export function averageFret(ps: readonly FretPos[]): number | undefined {
  const f = ps.filter((p) => p.fret > 0).map((p) => p.fret);
  if (f.length === 0) return undefined;
  return f.reduce((a, b) => a + b, 0) / f.length;
}

export interface TuningPreset {
  id: string;
  name: string;
  /** Sounding MIDI, high → low. */
  notes: number[];
}

export const GUITAR_TUNINGS: readonly TuningPreset[] = [
  { id: 'standard', name: 'Standard (E A D G B E)', notes: [64, 59, 55, 50, 45, 40] },
  { id: 'drop-d', name: 'Drop D (D A D G B E)', notes: [64, 59, 55, 50, 45, 38] },
  { id: 'dadgad', name: 'DADGAD', notes: [62, 57, 55, 50, 45, 38] },
  { id: 'open-g', name: 'Open G (D G D G B D)', notes: [62, 59, 55, 50, 43, 38] },
  { id: 'open-d', name: 'Open D (D A D F♯ A D)', notes: [62, 57, 54, 50, 45, 38] },
  { id: 'open-e', name: 'Open E (E B E G♯ B E)', notes: [64, 59, 56, 52, 47, 40] },
  { id: 'half-down', name: 'Half-step down (E♭ A♭ D♭ G♭ B♭ E♭)', notes: [63, 58, 54, 49, 44, 39] },
  { id: 'full-down', name: 'Whole-step down (D G C F A D)', notes: [62, 57, 53, 48, 43, 38] },
  { id: 'drop-c', name: 'Drop C (C G C F A D)', notes: [62, 57, 53, 48, 43, 36] },
];

export const BASS_TUNINGS: readonly TuningPreset[] = [
  { id: 'bass-standard', name: 'Standard (E A D G)', notes: [43, 38, 33, 28] },
  { id: 'bass-drop-d', name: 'Drop D (D A D G)', notes: [43, 38, 33, 26] },
  { id: 'bass-5', name: '5-string (B E A D G)', notes: [43, 38, 33, 28, 23] },
];

export const UKULELE_TUNINGS: readonly TuningPreset[] = [{ id: 'uke-standard', name: 'Standard (G C E A)', notes: [69, 64, 60, 67] }];
